/** เครื่องจักรของ Work Center หนึ่งแห่ง — ดู/เพิ่ม/แก้ชื่อ-ค่าเครื่อง/ปิด-เปิดใช้งาน/พิมพ์ป้าย QR */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { fetchRecords, patchRecord, postRecord } from '../../lib/backend/records';
import { stockFailureText, type StockRequestOptions } from '../../lib/inventory/stock';
import { parseMachine, type Machine } from '../../lib/machines/types';
import { MachineLabelSheet } from './MachineLabelSheet';

const RULE_TEXT: Readonly<Record<string, string>> = {
  WORK_CENTER_NOT_FOUND: 'ไม่พบ Work Center นี้ในโรงงาน',
  MACHINE_CODE_TAKEN: 'รหัสเครื่องนี้ถูกใช้แล้วในโรงงาน',
};

const DECIMAL = /^\d+(\.\d+)?$/;

export interface MachinePanelProps {
  readonly plantCode: string;
  readonly workCenterCode: string;
  readonly canCreate: boolean;
  readonly canEdit: boolean;
  readonly io: StockRequestOptions;
  readonly onClose: () => void;
}

function failureText(result: Parameters<typeof stockFailureText>[0]): string {
  if (result.status === 'rejected' && result.rule !== null && RULE_TEXT[result.rule] !== undefined) {
    return RULE_TEXT[result.rule] as string;
  }
  return stockFailureText(result);
}

export function MachinePanel({ plantCode, workCenterCode, canCreate, canEdit, io, onClose }: MachinePanelProps) {
  const [machines, setMachines] = useState<readonly Machine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState({ machine_code: '', name: '', rate: '' });
  const [editing, setEditing] = useState<{ id: string; name: string; rate: string } | null>(null);
  const [labels, setLabels] = useState<readonly Machine[] | null>(null);

  const { apiBaseUrl, fetchImpl } = io;
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchRecords(
        '/v1/machines',
        { plant_code: plantCode, work_center_code: workCenterCode },
        {
          ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
          ...(fetchImpl === undefined ? {} : { fetchImpl }),
        },
      );
      if (cancelled) {
        return;
      }
      if (result.status !== 'ready') {
        setMachines([]);
        setError(failureText(result));
        return;
      }
      setError(null);
      setMachines(result.items.map(parseMachine).filter((m): m is Machine => m !== null));
    })();
    return () => {
      cancelled = true;
    };
  }, [plantCode, workCenterCode, apiBaseUrl, fetchImpl, reload]);

  const refresh = useCallback(() => {
    setReload((n) => n + 1);
  }, []);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const code = form.machine_code.trim().toUpperCase();
    const name = form.name.trim();
    const rate = form.rate.trim();
    if (code === '' || name === '') {
      setError('กรอกรหัสเครื่องและชื่อ');
      return;
    }
    if (rate !== '' && !DECIMAL.test(rate)) {
      setError('ค่าเครื่อง บาท/ชม. ต้องเป็นตัวเลข');
      return;
    }
    const result = await postRecord(
      '/v1/machines',
      {
        plant_code: plantCode,
        work_center_code: workCenterCode,
        machine_code: code,
        name,
        ...(rate === '' ? {} : { machine_rate_per_hour: rate }),
      },
      {},
      io,
    );
    if (result.status !== 'ready') {
      setError(failureText(result));
      return;
    }
    setForm({ machine_code: '', name: '', rate: '' });
    refresh();
  };

  const patch = async (id: string, body: Record<string, unknown>) => {
    setError(null);
    const result = await patchRecord(`/v1/machines/${encodeURIComponent(id)}`, body, {}, io);
    if (result.status !== 'ready') {
      setError(failureText(result));
      return false;
    }
    refresh();
    return true;
  };

  const saveEdit = async () => {
    if (editing === null) {
      return;
    }
    const name = editing.name.trim();
    const rate = editing.rate.trim();
    if (name === '') {
      setError('ชื่อเครื่องห้ามว่าง');
      return;
    }
    if (rate !== '' && !DECIMAL.test(rate)) {
      setError('ค่าเครื่อง บาท/ชม. ต้องเป็นตัวเลข');
      return;
    }
    if (await patch(editing.id, { name, machine_rate_per_hour: rate === '' ? null : rate })) {
      setEditing(null);
    }
  };

  return (
    <section className="cap-card" data-testid="machine-panel" aria-label={`เครื่องจักรของ ${workCenterCode}`}>
      <div className="cap-page__head">
        <h2 className="cap-page__title">
          เครื่องจักรของ {workCenterCode} ({plantCode})
        </h2>
        <button type="button" className="cap-button" onClick={onClose}>
          ปิด
        </button>
      </div>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="machine-error">
          {error}
        </p>
      )}

      <div className="cap-table-wrap">
        <table className="cap-table" data-testid="machine-table">
          <thead>
            <tr>
              <th>รหัสเครื่อง</th>
              <th>ชื่อ</th>
              <th>สถานะ</th>
              <th>ค่าเครื่อง บาท/ชม.</th>
              <th>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {machines.length === 0 ? (
              <tr>
                <td colSpan={5}>ยังไม่มีเครื่องจักรใน Work Center นี้</td>
              </tr>
            ) : (
              machines.map((machine) => (
                <tr key={machine.machine_id} data-testid={`machine-row-${machine.machine_code}`}>
                  <td>{machine.machine_code}</td>
                  <td>
                    {editing?.id === machine.machine_id ? (
                      <input
                        className="cap-input"
                        aria-label="ชื่อเครื่อง"
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    ) : (
                      machine.name
                    )}
                  </td>
                  <td>{machine.status === 'ACTIVE' ? 'ใช้งาน' : 'ปิดใช้งาน'}</td>
                  <td>
                    {editing?.id === machine.machine_id ? (
                      <input
                        className="cap-input"
                        aria-label="ค่าเครื่อง บาท/ชม."
                        value={editing.rate}
                        onChange={(e) => setEditing({ ...editing, rate: e.target.value })}
                      />
                    ) : (
                      (machine.machine_rate_per_hour ?? '—')
                    )}
                  </td>
                  <td>
                    <button type="button" className="cap-button" onClick={() => setLabels([machine])}>
                      พิมพ์ QR
                    </button>
                    {canEdit &&
                      (editing?.id === machine.machine_id ? (
                        <>
                          <button type="button" className="cap-button cap-button--primary" onClick={() => void saveEdit()}>
                            บันทึก
                          </button>
                          <button type="button" className="cap-button" onClick={() => setEditing(null)}>
                            ยกเลิก
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="cap-button"
                            onClick={() =>
                              setEditing({
                                id: machine.machine_id,
                                name: machine.name,
                                rate: machine.machine_rate_per_hour ?? '',
                              })
                            }
                          >
                            แก้ไข
                          </button>
                          <button
                            type="button"
                            className="cap-button"
                            onClick={() =>
                              void patch(machine.machine_id, {
                                status: machine.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                              })
                            }
                          >
                            {machine.status === 'ACTIVE' ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                          </button>
                        </>
                      ))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {machines.length > 0 && (
        <div className="cap-form__actions cap-form__actions--padded">
          <button type="button" className="cap-button" data-testid="machine-print-all" onClick={() => setLabels(machines)}>
            พิมพ์ QR ทั้งหมด ({machines.length})
          </button>
        </div>
      )}

      {labels !== null && (
        <MachineLabelSheet
          machines={labels}
          onClose={() => {
            setLabels(null);
          }}
        />
      )}

      {canCreate && (
        <form className="cap-form" data-testid="machine-form" onSubmit={(e) => void add(e)}>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">รหัสเครื่อง</span>
              <input
                className="cap-input"
                aria-label="Machine: รหัส"
                value={form.machine_code}
                onChange={(e) => setForm((f) => ({ ...f, machine_code: e.target.value }))}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ชื่อเครื่อง</span>
              <input
                className="cap-input"
                aria-label="Machine: ชื่อ"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ค่าเครื่อง บาท/ชม. (ไม่บังคับ)</span>
              <input
                className="cap-input"
                aria-label="Machine: ค่าเครื่อง"
                value={form.rate}
                onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
              />
            </label>
          </div>
          <div className="cap-form__actions cap-form__actions--padded">
            <button type="submit" className="cap-button cap-button--primary">
              เพิ่มเครื่องจักร
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
